import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  BsPencilFill,
  BsFillTrashFill,
  BsPlayCircleFill,
} from "react-icons/bs";
import { fetchAdminVideosAction, deleteAdminVideoAction } from "../../redux/Actions";
import {
  isFromEditVideo,
  isFromEditVideoData,
} from "../../redux/Reducers/AdminVideoReducer";
import { strings, paths } from "../../utils/constants";
import AdminPagination from "../AdminPagination";
import ResponsiveDialog from "../Modal";
import NoRecordFound from "../NoRecordFound";
import VideoPlayerModal from "./VideoPlayerModal";
import { formatDuration, formatFileSize, formatDate } from "./videoHelpers";
import "../FormManagementTable/FormManagementTable.css";
import "./VideoManagement.css";

const RECORDS_PER_PAGE = 10;

/**
 * Extract the array of video rows from the API payload, tolerating a couple
 * of common response shapes ({ data: [...] } or { data: { items: [...] } }).
 */
const extractRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.items)) return payload.data.items;
  if (Array.isArray(payload.videos)) return payload.videos;
  return [];
};

/**
 * Read total count for backend-driven pagination when present, otherwise
 * fall back to the length of the current list (client pagination).
 */
const extractTotal = (payload, fallback) => {
  return (
    payload?.data?.total ??
    payload?.total ??
    payload?.pagination?.total ??
    fallback
  );
};

export default function VideoTable({ registerUploadTrigger }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fetchAdminVideosData, isLoading } = useSelector(
    (state) => state.adminVideoReducer
  );

  const [currentPage, setCurrentPage] = useState(1);

  // Modal state (playback + delete confirmation only; upload/edit are pages)
  const [playerOpen, setPlayerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const rows = extractRows(fetchAdminVideosData);
  const total = extractTotal(fetchAdminVideosData, rows.length);

  const fetchVideos = () => {
    dispatch(
      fetchAdminVideosAction({ page: currentPage, limit: RECORDS_PER_PAGE })
    );
  };

  useEffect(() => {
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Expose the "upload video" action to the parent page header button.
  useEffect(() => {
    if (registerUploadTrigger) {
      registerUploadTrigger(() => {
        dispatch(isFromEditVideo(false));
        dispatch(isFromEditVideoData({}));
        navigate(paths.adminRoutes.UPLOAD_VIDEO);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerUploadTrigger]);

  // Client-side slice when backend does not paginate.
  const nPage = Math.ceil(total / RECORDS_PER_PAGE);
  const isClientPaginated = rows.length > RECORDS_PER_PAGE;
  const visibleRows = isClientPaginated
    ? rows.slice(
      (currentPage - 1) * RECORDS_PER_PAGE,
      currentPage * RECORDS_PER_PAGE
    )
    : rows;

  const nextPage = () => currentPage < nPage && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const changePage = (id) => setCurrentPage(id);

  // Action handlers
  const handlePreview = (video) => {
    setSelectedVideo(video);
    setPlayerOpen(true);
  };

  const handleEdit = (video) => {
    dispatch(isFromEditVideo(true));
    dispatch(isFromEditVideoData(video));
    navigate(paths.adminRoutes.EDIT_VIDEO);
  };

  const handleDelete = (video) => {
    setSelectedVideo(video);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedVideo) return;
    const res = await dispatch(deleteAdminVideoAction(selectedVideo.id));
    setDeleteOpen(false);
    setSelectedVideo(null);
    if (res.payload?.success || res.payload?.data || res.payload?.message) {
      fetchVideos();
    }
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setSelectedVideo(null);
  };

  const statusClass = (status) =>
    (status || "").toLowerCase() === "inactive"
      ? "flabel flabel-draft"
      : "flabel flabel-Active";

  return (
    <>
      <div className="ftable-wrapper vm-table-wrapper">
        <table className="ftable">
          <thead>
            <tr>
              <th>{strings.general.SR_NO}</th>
              <th>{strings.video.VIDEO_TITLE_EN}</th>
              <th>{strings.video.VIDEO_TITLE_FR}</th>
              <th>{strings.video.FRENCH_VIDEO_COLUMN}</th>
              <th>{strings.video.FILE_NAME_EN}</th>
              <th>{strings.video.FILE_NAME_FR}</th>
              <th>{strings.video.DURATION_EN}</th>
              <th>{strings.video.DURATION_FR}</th>
              <th>{strings.video.FILE_SIZE_EN}</th>
              <th>{strings.video.FILE_SIZE_FR}</th>
              <th>{strings.video.STATUS}</th>
              <th>{strings.video.CREATED_DATE}</th>
              <th>{strings.video.UPDATED_DATE}</th>
              <th>{strings.words.ACTIONS}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="table-cell" colSpan={14} align="center">
                  {strings.video.LOADING_VIDEOS}
                </td>
              </tr>
            ) : visibleRows.length > 0 ? (
              visibleRows.map((row, idx) => {
                const fileNameFr = row.fileNameFr || row.file_name_fr;
                const fileSizeFr = row.fileSizeFr ?? row.file_size_fr;
                const durationFr = row.durationFr ?? row.duration_fr;
                const hasFr = !!(row.hasFrVideo || fileNameFr);

                return (
                  <tr key={row.id ?? idx}>
                    <td className="table-cell">
                      {(currentPage - 1) * RECORDS_PER_PAGE + idx + 1}
                    </td>
                    <td className="table-cell w-25">{row.title || "-"}</td>
                    <td className="table-cell w-25">
                      {row.titleFr || row.title_fr || "-"}
                    </td>
                    <td className="table-cell">
                      {hasFr ? (
                        <span className="flabel flabel-Active">
                          {strings.video.FR_VIDEO_YES}
                        </span>
                      ) : (
                        <span className="flabel flabel-draft">
                          {strings.video.FR_VIDEO_NO}
                        </span>
                      )}
                    </td>
                    <td className="table-cell w-25">
                      {row.fileName || row.file_name || "-"}
                    </td>
                    <td className="table-cell w-25">
                      {hasFr ? fileNameFr || "-" : "-"}
                    </td>
                    <td className="table-cell">
                      {formatDuration(row.duration)}
                    </td>
                    <td className="table-cell">
                      {hasFr ? formatDuration(durationFr) : "-"}
                    </td>
                    <td className="table-cell">
                      {formatFileSize(row.fileSize ?? row.file_size)}
                    </td>
                    <td className="table-cell">
                      {hasFr ? formatFileSize(fileSizeFr) : "-"}
                    </td>
                    <td className="table-cell">
                      <span className={statusClass(row.status)}>
                        {row.status || strings.video.ACTIVE}
                      </span>
                    </td>
                    <td className="table-cell w-25">
                      {formatDate(row.createdAt || row.created_at)}
                    </td>
                    <td className="table-cell w-25">
                      {formatDate(row.updatedAt || row.updated_at)}
                    </td>
                    <td className="fit">
                      <span className="factions mt-1">
                        <BsPlayCircleFill
                          title={strings.video.PREVIEW}
                          onClick={() => handlePreview(row)}
                          className="cursor-pointer"
                          style={{ color: "#184437" }}
                        />
                        <BsPencilFill
                          title={strings.video.EDIT_VIDEO}
                          onClick={() => handleEdit(row)}
                          className="cursor-pointer"
                          style={{ color: "#184437" }}
                        />
                        <BsFillTrashFill
                          title={strings.video.DELETE_VIDEO_TITLE}
                          onClick={() => handleDelete(row)}
                          className="fdelete-btn cursor-pointer"
                        />
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <NoRecordFound colSpan={14} />
            )}
          </tbody>
        </table>

        {!isLoading && total > RECORDS_PER_PAGE && (
          <AdminPagination
            currentPage={currentPage}
            totalRecords={total}
            nextPage={nextPage}
            prevPage={prevPage}
            changePage={changePage}
          />
        )}
      </div>

      {/* Playback */}
      <VideoPlayerModal
        open={playerOpen}
        video={selectedVideo}
        onClose={() => setPlayerOpen(false)}
      />

      {/* Delete confirmation - reuses the shared ResponsiveDialog */}
      <ResponsiveDialog
        open={deleteOpen}
        handleClose={closeDelete}
        negativeButtonClickHandler={closeDelete}
        positiveButtonClickHandler={confirmDelete}
        titleText={strings.video.DELETE_VIDEO_TITLE}
        subTitleText={`${strings.video.DELETE_VIDEO_CONFIRM} "${selectedVideo?.title || ""
          }"? ${strings.video.DELETE_IRREVERSIBLE}`}
        negativeButtonText={strings.words.CANCEL}
        positiveButtonText={strings.words.YES}
      />
    </>
  );
}
